import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone'; // 🟢 Limpiamos el alias de depuración
import toast from 'react-hot-toast';
import EmojiPickerWrapper from '../common/EmojiPickerWrapper'; // 🟢 Importamos el wrapper optimizado

const RepostedPostPreview = ({ post }) => {
  if (!post) return null;
  return (
    <div className="mt-2 p-2 border border-gray-200 dark:border-gray-700 rounded-lg">
      <div className="flex items-center space-x-2">
        <img src={post.author.avatar} alt={post.author.username} className="w-6 h-6 rounded-full" />
        <span className="font-semibold text-sm text-gray-800 dark:text-gray-100">{post.author.username}</span>
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 line-clamp-2">{post.content}</p>
      {post.images?.[0] && (
        <img src={post.images[0]} alt="Repost preview" className="mt-2 w-20 h-20 object-cover rounded-md" />
      )}
    </div>
  );
};

const CreatePostModal = ({ isOpen, onClose, modalData }) => {
  // 🟢 1. Desestructuramos los datos del modal
  const { onPostSubmit, wallOwnerId, repostedPost } = modalData || {}; // 🟢 SOLUCIÓN: Añadimos `|| {}` para seguridad.

  const [content, setContent] = useState('');
  const [images, setImages] = useState([]);
  const [privacy, setPrivacy] = useState('public'); // Estado para la privacidad
  const [isPublishing, setIsPublishing] = useState(false);
  const [isPickerVisible, setIsPickerVisible] = useState(false); // 🟢 Estado para emojis
  const textAreaRef = useRef(null); // 🟢 Referencia para insertar emojis

  // 🟢 2. Limpiamos el estado si el modal se abre o cierra
  useEffect(() => {
    if (!isOpen) {
      setContent('');
      setImages([]);
      setPrivacy('public');
      setIsPickerVisible(false);
    }
  }, [isOpen]);

  const onDrop = useCallback((acceptedFiles) => {
    const newImages = acceptedFiles.map((file) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
        file: file, // Guardamos el archivo original para el FormData
      })
    );
    setImages((prev) => [...prev, ...newImages].slice(0, 10));
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': [] },
  });

  const removeImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index)); // Corregido para que funcione
  };

  // 🟢 Función optimizada para insertar emojis en la posición del cursor
  const handleEmojiClick = (emojiObject) => {
    const { selectionStart, selectionEnd } = textAreaRef.current;
    const newText = content.substring(0, selectionStart) + emojiObject.emoji + content.substring(selectionEnd);
    setContent(newText);
    const newCursorPosition = selectionStart + emojiObject.emoji.length;
    setTimeout(() => {
      textAreaRef.current.focus();
      textAreaRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
    }, 0);
  };

  const handlePublish = async () => {
    if (!content.trim() && images.length === 0) {
      toast.error('Añade contenido o una imagen para publicar.');
      return;
    }
    setIsPublishing(true);
    // [🕵️‍♂️ TRACE 1] Datos que salen del modal.
    console.log(
      '[🕵️‍♂️ TRACE 1 - CreatePostModal] Enviando datos para crear post:',
      { content, wallOwnerId, privacy }
    );
    // 🟢 3. Incluimos el ID del post citado si existe
    const result = await onPostSubmit({
      content,
      images,
      wallOwnerId,
      privacy,
      repostedPostId: repostedPost?._id,
    });
    if (result.success) {
      handleClose();
    }
    setIsPublishing(false);
  };

  const handleClose = () => {
    // La limpieza ahora se maneja en el useEffect
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 dark:bg-black/75 flex items-center justify-center z-50"
      onClick={handleClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl flex flex-col overflow-hidden w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center p-3 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100">Crear Publicación</h2>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
          >
            <i className="fas fa-times text-gray-600 dark:text-gray-300"></i>
          </button>
        </div>

        <div className="p-4 space-y-4">
          <textarea
            ref={textAreaRef} // 🟢 Vinculamos la referencia
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={repostedPost ? "Añade un comentario..." : "¿Qué estás pensando? tu"}
            className="w-full p-2 border-none resize-none focus:ring-0 text-lg bg-transparent text-gray-900 dark:text-gray-100"
            rows="4"
          />
          <div
            {...getRootProps()}
            className={`p-4 border-2 border-dashed rounded-lg text-center cursor-pointer ${isDragActive ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30' : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
          >
            <input {...getInputProps()} />
            <div className="flex items-center justify-center text-gray-500 dark:text-gray-400">
              <i className="fas fa-images text-2xl mr-3"></i>
              <p>Arrastra imágenes aquí o haz clic para seleccionar</p>
            </div>
          </div>
          {images.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
              {images.map((image, index) => (
                <div key={index} className="relative group">
                  <img
                    src={image.preview}
                    alt="preview"
                    className="w-full h-24 object-cover rounded-md"
                  />
                  <button
                    onClick={() => removeImage(index)}
                    className="absolute top-1 right-1 bg-black bg-opacity-60 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              ))}
            </div>
          )}
          {/* 🟢 4. Mostramos la vista previa del post citado */}
          <RepostedPostPreview post={repostedPost} />
        </div>

        {/* Barra de Herramientas Inferior (Privacidad + Emojis) */}
        <div className="flex items-center justify-between px-4 pb-2">
          {/* Selector de Privacidad */}
          <div className="flex items-center space-x-2">
            <i className="fas fa-globe-americas text-gray-500 dark:text-gray-400"></i>
            <select
              value={privacy}
              onChange={(e) => setPrivacy(e.target.value)}
              className="text-sm font-semibold text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 border-none rounded-md p-1 focus:ring-0 cursor-pointer"
            >
              <option value="public">Público</option>
              <option value="friends">Amigos</option>
              <option value="private">Solo yo</option>
            </select>
          </div>

          {/* 🟢 Botón de Emojis */}
          <div className="relative">
            <button 
              onClick={() => setIsPickerVisible(!isPickerVisible)} 
              className="text-gray-500 dark:text-gray-400 hover:text-yellow-500 transition-colors p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
              title="Añadir emoji"
            >
              <i className="fas fa-smile text-xl"></i>
            </button>
            {isPickerVisible && (
              <EmojiPickerWrapper onEmojiClick={handleEmojiClick} onClose={() => setIsPickerVisible(false)} />
            )}
          </div>
        </div>

        <div className="p-3 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <button
            onClick={handlePublish}
            disabled={isPublishing || (!content.trim() && images.length === 0)}
            className="bg-blue-600 text-white font-bold py-2 px-6 rounded-lg hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed"
          >
            {isPublishing ? (
              <>
                <i className="fas fa-spinner fa-spin mr-2"></i>Publicando...
              </>
            ) : (
              'Publicar'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreatePostModal;
